import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import cn from 'classnames';

import { ImagePlaceholder } from '@notino/react-styleguide';
import { ExperimentMoodPhotosVariant } from '@notino/shared/definitions/types';

import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { IImageProperties } from '@helpers/utils';
import { useIsMounted } from '@hooks/useIsMounted';
import { useOnTap } from '@hooks/useOnTap';
import { assignRefs } from '@utils/assignRefs';

import { trackProductImageTap } from '../tracking';

import {
  SlideWrapper,
  ImgWrap,
  dividerSlideWrapperClass,
  squareSlideWrapperClass as moodPhotoSlideWrapperClass,
  notSquareSlideWrapperClass as notMoodPhotoSlideWrapper,
} from './styled';

interface Props {
  isFirstSlide?: boolean;
  isLastSlide?: boolean;
  imgProps: IImageProperties;
}

export const Slide: React.FC<React.PropsWithChildren<Props>> = ({
  imgProps,
  children,
  isFirstSlide = false,
  isLastSlide = false,
}) => {
  const imageAlt = imgProps.alt?.trim() || 'Product image';
  const { experiment_pd_mood_photos_variant } = useFeatureFlags();
  const [isMoodPhoto, setIsMoodPhoto] = React.useState(() => {
    switch (experiment_pd_mood_photos_variant) {
      case ExperimentMoodPhotosVariant.AllMood:
        return true;
      case ExperimentMoodPhotosVariant.AllMoodExceptFirst:
        return !isFirstSlide;
      default:
        return false;
    }
  });
  const isMounted = useIsMounted();
  const [inViewRef, inView] = useInView({ triggerOnce: true });

  const { getTimeFromInit } = useTrackingContext();
  const onTapRef = useOnTap<HTMLDivElement>(() =>
    trackProductImageTap({ timing: getTimeFromInit() })
  );

  const handleImageLoad = (elem: HTMLImageElement) => {
    if (
      experiment_pd_mood_photos_variant !== ExperimentMoodPhotosVariant.Default
    ) {
      return;
    }
    setIsMoodPhoto(
      elem.naturalWidth === elem.naturalHeight &&
        !isFirstSlide &&
        !imgProps.src.includes('-o/')
    );
  };

  const checkIfImageLoadedBeforeJavascript = (instance: HTMLImageElement) => {
    if (instance?.complete) {
      handleImageLoad(instance);
    }
  };

  return (
    <SlideWrapper
      ref={assignRefs(onTapRef, inViewRef)}
      className={cn({
        [dividerSlideWrapperClass]: !isMounted && !isLastSlide,
        [moodPhotoSlideWrapperClass]: isMoodPhoto,
        [notMoodPhotoSlideWrapper]: !isMoodPhoto,
      })}
    >
      {!inView && (
        <ImgWrap>
          <img
            {...imgProps}
            alt={imageAlt}
            draggable={false}
            loading={isFirstSlide ? 'eager' : 'lazy'}
            ref={checkIfImageLoadedBeforeJavascript}
            onLoad={(e) => handleImageLoad(e.currentTarget)}
          />
        </ImgWrap>
      )}

      {inView && (
        <ImagePlaceholder
          image={{ ...imgProps, alt: imageAlt }}
          onLoad={handleImageLoad}
          hidePlaceholder={isFirstSlide}
        />
      )}

      {children}
    </SlideWrapper>
  );
};
